import { Col, DatePicker, Form, Input, Modal, notification, Row, Select } from 'antd';
import Button from 'mixins/ui/button/button';
import React, { FC, useState } from 'react';
import {
    changeAdminTokenRequestStatus,
    downloadAdminTokenRequestAttachment,
    exportAdminTokenRequests,
    getAdminTokenRequestById,
    issueAdminTokenRequest,
} from '../actions';
import { useTokenRequests } from '../hooks/use-token-requests';
import {
    ApiTokenRequestDto,
    ApiTokenRequestJournalType,
    ApiTokenRequestStatus,
    ChangeApiTokenRequestStatusData,
} from '../types';
import { API_TOKEN_REQUEST_STATUS_LABELS } from '../utils/constants';
import {
    getRequestErrorMessage,
    getResponseFileName,
    saveBlob,
} from '../utils/token-request-helpers';
import { TokenRequestDetailsModal } from './token-request-details-modal';
import { TokenRequestStatusModal } from './token-request-status-modal';
import { TokenRequestsTable } from './token-requests-table';

interface TokenRequestsTabProps {
    journalType: ApiTokenRequestJournalType;
    canManage: boolean;
}

interface FilterFormValues {
    search?: string;
    status?: ApiTokenRequestStatus;
    period?: any[];
}

export const TokenRequestsTab: FC<TokenRequestsTabProps> = ({
    journalType,
    canManage,
}) => {
    const [filterForm] = Form.useForm();
    const {
        applyFilters,
        data,
        fetchData,
        filters,
        handlePageChange,
        handlePageSizeChange,
        isLoading,
        pagination,
        resetFilters,
    } = useTokenRequests(journalType);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] =
        useState<ApiTokenRequestDto | null>(null);
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [statusModalRequest, setStatusModalRequest] =
        useState<ApiTokenRequestDto | null>(null);
    const [statusModalInitialStatus, setStatusModalInitialStatus] = useState<
        Exclude<ApiTokenRequestStatus, 'TOKEN_ISSUED'> | undefined
    >();
    const [actionLoading, setActionLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const openDetails = (request: ApiTokenRequestDto) => {
        setDetailsVisible(true);
        setDetailsLoading(true);
        setSelectedRequest(null);

        getAdminTokenRequestById(request.id)
            .then((response) => setSelectedRequest(response.data))
            .catch((error) => {
                notification.error({
                    message: 'Ошибка',
                    description: getRequestErrorMessage(error),
                });
                setDetailsVisible(false);
            })
            .finally(() => setDetailsLoading(false));
    };

    const openStatusModal = (
        request: ApiTokenRequestDto,
        status?: Exclude<ApiTokenRequestStatus, 'TOKEN_ISSUED'>
    ) => {
        setStatusModalRequest(request);
        setStatusModalInitialStatus(status);
        setStatusModalVisible(true);
    };

    const refreshDetails = (id: number) => {
        if (!detailsVisible) {
            return Promise.resolve();
        }

        return getAdminTokenRequestById(id).then((response) => {
            setSelectedRequest(response.data);
        });
    };

    const changeStatus = (payload: ChangeApiTokenRequestStatusData) => {
        setActionLoading(true);

        changeAdminTokenRequestStatus(payload)
            .then(() => {
                notification.success({
                    message: 'Статус заявки успешно изменён',
                });
                setStatusModalVisible(false);
                return Promise.all([fetchData(), refreshDetails(payload.id)]);
            })
            .catch((error) => {
                notification.error({
                    message: 'Ошибка',
                    description: getRequestErrorMessage(error),
                });
            })
            .finally(() => setActionLoading(false));
    };

    const issueToken = (request: ApiTokenRequestDto) => {
        Modal.confirm({
            title: 'Выдать API-токен?',
            content: `Будет выдан токен по заявке ${request.requestNumber}.`,
            okText: 'Выдать',
            cancelText: 'Отменить',
            onOk: () => {
                setActionLoading(true);

                return issueAdminTokenRequest(request.id)
                    .then(() => {
                        notification.success({
                            message: 'API-токен успешно выдан',
                        });
                        return Promise.all([
                            fetchData(),
                            refreshDetails(request.id),
                        ]);
                    })
                    .catch((error) => {
                        notification.error({
                            message: 'Ошибка',
                            description: getRequestErrorMessage(error),
                        });
                    })
                    .finally(() => setActionLoading(false));
            },
        });
    };

    const downloadAttachment = (
        request: ApiTokenRequestDto,
        attachmentId: number,
        fileName: string
    ) => {
        downloadAdminTokenRequestAttachment(request.id, attachmentId)
            .then((response) => {
                saveBlob(
                    response.data,
                    getResponseFileName(response, fileName)
                );
            })
            .catch((error) => {
                notification.error({
                    message: 'Ошибка',
                    description: getRequestErrorMessage(error),
                });
            });
    };

    const submitFilters = (values: FilterFormValues) => {
        const [dateFrom, dateTo] = values.period || [];

        applyFilters({
            search: values.search?.trim() || undefined,
            status:
                journalType === 'SUBMITTED' ? values.status : undefined,
            dateFrom: dateFrom?.format?.('YYYY-MM-DD'),
            dateTo: dateTo?.format?.('YYYY-MM-DD'),
        });
    };

    const clearFilters = () => {
        filterForm.resetFields();
        resetFilters();
    };

    const exportJournal = () => {
        setExportLoading(true);

        exportAdminTokenRequests({
            type: journalType,
            ...filters,
            sourceUrl:
                typeof window === 'undefined' ? '' : window.location.href,
        })
            .then((response) => {
                saveBlob(
                    response.data,
                    getResponseFileName(
                        response,
                        'api-token-requests.xlsx'
                    )
                );
            })
            .catch((error) => {
                notification.error({
                    message: 'Ошибка',
                    description: getRequestErrorMessage(error),
                });
            })
            .finally(() => setExportLoading(false));
    };

    return (
        <div className="admin-token-generations__requests-tab">
            <Form
                form={filterForm}
                layout="vertical"
                onFinish={submitFilters}
                className="admin-token-generations__filters"
            >
                <Row gutter={[16, 8]} align="bottom">
                    <Col xs={24} md={journalType === 'SUBMITTED' ? 7 : 9}>
                        <Form.Item label="Поиск" name="search">
                            <Input
                                size="large"
                                allowClear
                                placeholder="№ обращения, ИИН/БИН, ФИО, телефон, email..."
                            />
                        </Form.Item>
                    </Col>

                    {journalType === 'SUBMITTED' ? (
                        <Col xs={24} md={5}>
                            <Form.Item label="Статус" name="status">
                                <Select
                                    size="large"
                                    allowClear
                                    placeholder="Все статусы"
                                >
                                    {Object.entries(
                                        API_TOKEN_REQUEST_STATUS_LABELS
                                    ).map(([status, label]) => (
                                        <Select.Option
                                            key={status}
                                            value={status}
                                        >
                                            {label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    ) : null}

                    <Col xs={24} md={7}>
                        <Form.Item label="Дата подачи" name="period">
                            <DatePicker.RangePicker
                                size="large"
                                format="DD.MM.YYYY"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={journalType === 'SUBMITTED' ? 5 : 8}>
                        <Form.Item>
                            <div className="admin-token-generations__filter-actions">
                                <Button onClick={() => filterForm.submit()}>
                                    Найти
                                </Button>
                                <Button onClick={clearFilters}>Сбросить</Button>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                <Row justify="end">
                    <Col>
                        <Button
                            color="gold"
                            loading={exportLoading}
                            onClick={exportJournal}
                        >
                            Экспорт в Excel
                        </Button>
                    </Col>
                </Row>
            </Form>

            <TokenRequestsTable
                journalType={journalType}
                data={data}
                isLoading={isLoading}
                pagination={pagination}
                canManage={canManage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onView={openDetails}
                onChangeStatus={openStatusModal}
                onIssue={issueToken}
            />

            <TokenRequestDetailsModal
                visible={detailsVisible}
                loading={detailsLoading}
                actionLoading={actionLoading}
                request={selectedRequest}
                canManage={canManage}
                onCancel={() => setDetailsVisible(false)}
                onChangeStatus={changeStatus}
                onIssue={issueToken}
                onDownloadAttachment={downloadAttachment}
            />

            <TokenRequestStatusModal
                visible={statusModalVisible}
                request={statusModalRequest}
                initialStatus={statusModalInitialStatus}
                loading={actionLoading}
                onCancel={() => setStatusModalVisible(false)}
                onSubmit={changeStatus}
            />
        </div>
    );
};